import type { Personnel } from '../../personnel/types';
import type {
  CommunicationAudienceSelection,
  CommunicationLinkedSource,
  CommunicationRecipientSnapshot,
  CommunicationRecipientSource,
  ResolvedCommunicationRecipient,
} from './types';

export interface DeliveryEmailRecipient {
  email: string;
  name: string;
  selected: boolean;
}

export interface ExternalRecipientDraft {
  email: string;
  name?: string;
}

export type RemovedDeliveryRecipient = Pick<
  ResolvedCommunicationRecipient,
  'email' | 'name' | 'personnelId' | 'sourceLabels' | 'sources'
>;

interface SnapshotFromExplicitRecipientsInput {
  snapshot?: CommunicationRecipientSnapshot;
  recipients: DeliveryEmailRecipient[];
  createdAt: string;
  linkedSource?: CommunicationLinkedSource;
}

const MANUAL_RECIPIENT_SOURCE_LABEL = 'Manual recipients';
const MANUAL_RECIPIENT_SOURCE = {
  type: 'external',
  audienceId: 'manual-review',
  audienceName: MANUAL_RECIPIENT_SOURCE_LABEL,
} as const;

function normalizeEmail(email: string | undefined): string {
  return email?.trim().toLowerCase() ?? '';
}

function manualRecipientSources(email: string): CommunicationRecipientSource[] {
  return [
    {
      ...MANUAL_RECIPIENT_SOURCE,
      email,
    },
  ];
}

export function normalizeDeliveryRecipients(value: DeliveryEmailRecipient[]): DeliveryEmailRecipient[] {
  const seenEmails = new Set<string>();
  const recipients: DeliveryEmailRecipient[] = [];

  for (const recipient of value) {
    const email = normalizeEmail(recipient.email);

    if (recipient.selected === false || !email || seenEmails.has(email)) {
      continue;
    }

    seenEmails.add(email);
    recipients.push({
      email,
      name: recipient.name.trim() || email,
      selected: true,
    });
  }

  return recipients;
}

export function emailRecipientsFromSnapshot(
  snapshot: CommunicationRecipientSnapshot
): DeliveryEmailRecipient[] {
  return normalizeDeliveryRecipients(
    snapshot.recipients.map((recipient) => ({
      email: recipient.email,
      name: recipient.name,
      selected: true,
    }))
  );
}

export function addExternalRecipientsToSnapshot(
  snapshot: CommunicationRecipientSnapshot,
  externalRecipients: ExternalRecipientDraft[],
  personnel: Personnel[]
): CommunicationRecipientSnapshot {
  const seenEmails = new Set(snapshot.recipients.map((recipient) => normalizeEmail(recipient.email)));
  const recipients = [...snapshot.recipients];
  let excludedRecipients = [...snapshot.excludedRecipients];
  const optedOutPersonnelByEmail = new Map<string, Personnel>();

  for (const person of personnel) {
    const email = normalizeEmail(person.email);

    if (email && !person.deleted && person.emailOptOut) {
      optedOutPersonnelByEmail.set(email, person);
    }
  }

  for (const externalRecipient of externalRecipients) {
    const email = normalizeEmail(externalRecipient.email);

    if (!email || seenEmails.has(email)) {
      continue;
    }

    seenEmails.add(email);
    const sourceLabels = [MANUAL_RECIPIENT_SOURCE_LABEL];
    const sources = manualRecipientSources(email);
    const suppressedContact = optedOutPersonnelByEmail.get(email);
    const name = externalRecipient.name?.trim() || email;

    if (suppressedContact) {
      const alreadyExcluded = excludedRecipients.some((recipient) => (
        normalizeEmail(recipient.email ?? '') === email
        && recipient.reason === 'Suppressed contact email'
      ));
      if (alreadyExcluded) {
        continue;
      }

      excludedRecipients.push({
        email,
        name,
        personnelId: suppressedContact.id,
        reason: 'Suppressed contact email',
        sourceLabels,
        sources,
      });
      continue;
    }

    excludedRecipients = excludedRecipients.filter((recipient) => !(
      normalizeEmail(recipient.email ?? '') === email
      && recipient.reason === 'Removed for this send'
    ));
    recipients.push({
      email,
      name,
      sourceLabels,
      sources,
    });
  }

  return {
    ...snapshot,
    recipients,
    excludedRecipients,
  };
}

export function removeRecipientsFromSnapshot(
  snapshot: CommunicationRecipientSnapshot,
  removedRecipients: RemovedDeliveryRecipient[]
): CommunicationRecipientSnapshot {
  const removedPersonnelIds = new Set(
    removedRecipients
      .map((recipient) => recipient.personnelId)
      .filter((personnelId): personnelId is string => Boolean(personnelId))
  );
  const removedEmails = new Set(
    removedRecipients
      .filter((recipient) => !recipient.personnelId)
      .map((recipient) => normalizeEmail(recipient.email))
      .filter(Boolean)
  );
  const recipients: ResolvedCommunicationRecipient[] = [];
  const excludedRecipients = [...snapshot.excludedRecipients];

  for (const recipient of snapshot.recipients) {
    const shouldRemove =
      (recipient.personnelId && removedPersonnelIds.has(recipient.personnelId)) ||
      removedEmails.has(normalizeEmail(recipient.email));

    if (!shouldRemove) {
      recipients.push(recipient);
      continue;
    }

    excludedRecipients.push({
      email: normalizeEmail(recipient.email),
      name: recipient.name,
      personnelId: recipient.personnelId,
      reason: 'Removed for this send',
      sourceLabels: [...recipient.sourceLabels],
      sources: [...recipient.sources],
    });
  }

  return {
    ...snapshot,
    recipients,
    excludedRecipients,
  };
}

export function snapshotFromExplicitRecipients({
  snapshot,
  recipients,
  createdAt,
  linkedSource,
}: SnapshotFromExplicitRecipientsInput): CommunicationRecipientSnapshot {
  const normalizedRecipients = normalizeDeliveryRecipients(recipients);

  return {
    createdAt: snapshot?.createdAt ?? createdAt,
    standingAudienceIds: [...(snapshot?.standingAudienceIds ?? [])],
    contextualAudienceKeys: [...(snapshot?.contextualAudienceKeys ?? [])],
    ...(snapshot?.linkedSource || linkedSource
      ? { linkedSource: snapshot?.linkedSource ?? linkedSource }
      : {}),
    recipients: normalizedRecipients.map((recipient) => ({
      email: recipient.email,
      name: recipient.name,
      sourceLabels: ['Explicit recipients'],
      sources: [],
    })),
    excludedRecipients: [],
    missingEmailRecipients: [],
  };
}

export function selectionFromSnapshot(
  snapshot?: CommunicationRecipientSnapshot
): CommunicationAudienceSelection | undefined {
  if (!snapshot) {
    return;
  }

  return {
    standingAudienceIds: [...snapshot.standingAudienceIds],
    contextualAudienceKeys: [...snapshot.contextualAudienceKeys],
  };
}
